import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, authApi } from '../services/api';
import { auth, googleProvider } from '../config/firebase';
import {
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  isAgeVerified: boolean;
  createdAt: string;
}

interface JapaStats {
  totalCount: number;
  todayCount: number;
  currentStreak: number;
  longestStreak: number;
  totalSessions: number;
}

interface AuthState {
  user: User | null;
  japaStats: JapaStats | null;
  lifePurposeProfile: any | null;
  hasLifePurposeAccess: boolean;
  canRetakeQuestionnaire: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGuest: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  verifyAge: (dateOfBirth: string) => Promise<{ success: boolean; isAgeVerified: boolean }>;
  refreshProfile: () => Promise<void>;
  setGuestMode: (isGuest: boolean) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(() => {
    return {
      user: null,
      japaStats: null,
      lifePurposeProfile: null,
      hasLifePurposeAccess: false,
      canRetakeQuestionnaire: false,
      isAuthenticated: false,
      isLoading: true,
      isGuest: localStorage.getItem('isGuest') === 'true',
    };
  });

  // Helper: get Firebase ID token and set it for API calls
  const setFirebaseToken = useCallback(async (): Promise<boolean> => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return false;
    const idToken = await firebaseUser.getIdToken(true);
    api.setAuthToken(idToken);
    return true;
  }, []);

  // Helper: fetch profile from backend and update state
  // Returns: { ok: true } on success, { ok: false, isAuthError: boolean } on failure
  const fetchAndSetProfile = useCallback(async (): Promise<{ ok: boolean; isAuthError: boolean }> => {
    const response = await authApi.getProfile();
    if (response.success && response.data) {
      setState({
        user: response.data.user,
        japaStats: response.data.japaStats,
        lifePurposeProfile: response.data.lifePurposeProfile,
        hasLifePurposeAccess: response.data.hasLifePurposeAccess,
        canRetakeQuestionnaire: response.data.canRetakeQuestionnaire ?? false,
        isAuthenticated: true,
        isLoading: false,
        isGuest: false,
      });
      return { ok: true, isAuthError: false };
    }
    // 401/403 = actual auth problem; anything else = server/network issue
    const isAuthError = response.status === 401 || response.status === 403;
    return { ok: false, isAuthError };
  }, []);

  // Helper: retry fetchAndSetProfile with backoff (for transient server/network errors)
  const fetchProfileWithRetry = useCallback(async (maxRetries = 2): Promise<{ ok: boolean; isAuthError: boolean }> => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const result = await fetchAndSetProfile();
      if (result.ok || result.isAuthError) return result;
      // Transient failure — wait before retry (1s, 2s)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 1000));
      }
    }
    return { ok: false, isAuthError: false };
  }, [fetchAndSetProfile]);

  // Listen for Firebase auth state changes (handles page refresh + existing sessions)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Force-refresh token to avoid sending expired/cached JWTs
          const idToken = await firebaseUser.getIdToken(true);
          api.setAuthToken(idToken);
          const result = await fetchProfileWithRetry(2);
          if (!result.ok) {
            if (result.isAuthError) {
              // Genuine auth failure (401/403) — token is invalid, clear it
              api.setAuthToken(null);
              setState(prev => ({ ...prev, isLoading: false }));
            } else {
              // Server/network error — try Google sync if applicable, otherwise keep token
              const isGoogle = firebaseUser.providerData.some(p => p.providerId === 'google.com');
              if (isGoogle) {
                const resp = await authApi.googleAuth(idToken);
                if (resp.success) {
                  const retryResult = await fetchAndSetProfile();
                  if (!retryResult.ok) {
                    setState(prev => ({ ...prev, isLoading: false }));
                  }
                } else {
                  setState(prev => ({ ...prev, isLoading: false }));
                }
              } else {
                // Email user + server error: keep the token, don't clear it
                setState(prev => ({ ...prev, isLoading: false }));
              }
            }
          }
        } catch {
          // Don't clear token on network errors — Firebase session is still valid
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } else {
        // No Firebase user — clear any stale stored token immediately.
        // Firebase is the source of truth. If it says no user, old tokens are useless.
        api.setAuthToken(null);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => unsubscribe();
  }, [fetchAndSetProfile, fetchProfileWithRetry]);

  // Login: use Firebase client SDK to verify password, get ID token
  const login = useCallback(async (email: string, password: string) => {
    try {
      // Firebase verifies the password client-side
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await credential.user.getIdToken(true);
      api.setAuthToken(idToken);
      localStorage.removeItem('isGuest');

      // Fetch profile from backend (with retry for cold starts)
      const result = await fetchProfileWithRetry(3);
      if (result.ok) {
        return { success: true };
      }
      // Firebase auth succeeded but backend failed — show diagnostic info
      const lastResponse = await authApi.getProfile();
      const diagInfo = lastResponse.status
        ? `(HTTP ${lastResponse.status}: ${lastResponse.error || 'unknown'})`
        : '(network error — server unreachable)';
      return {
        success: false,
        error: `Signed in, but could not load your profile ${diagInfo}. Please try again.`,
      };
    } catch (error: any) {
      const code = error?.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        return { success: false, error: 'Invalid email or password' };
      }
      if (code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many attempts. Please try again later.' };
      }
      return { success: false, error: `Login failed: ${code || error?.message || 'unknown error'}` };
    }
  }, [fetchProfileWithRetry]);

  // Register: backend creates Firebase user + DB user, returns custom token, we exchange it
  const register = useCallback(async (data: RegisterData) => {
    try {
      const response = await authApi.register(data);
      if (response.success && response.data) {
        // Exchange custom token for a real Firebase ID token
        const credential = await signInWithCustomToken(auth, response.data.token);
        const idToken = await credential.user.getIdToken();
        api.setAuthToken(idToken);
        localStorage.removeItem('isGuest');

        setState({
          user: response.data.user,
          japaStats: null,
          lifePurposeProfile: null,
          hasLifePurposeAccess: false,
          canRetakeQuestionnaire: false,
          isAuthenticated: true,
          isLoading: false,
          isGuest: false,
        });
        return { success: true };
      }
      return { success: false, error: response.error || 'Registration failed' };
    } catch (error) {
      return { success: false, error: 'Registration failed. Please try again.' };
    }
  }, []);

  // Google login: popup → get ID token → create/find user in backend
  const loginWithGoogle = useCallback(async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken(true);
      api.setAuthToken(idToken);
      localStorage.removeItem('isGuest');

      // Ensure user exists in backend (creates if new, finds if existing)
      await authApi.googleAuth(idToken);

      // Fetch full profile (with retry for cold starts)
      const profileResult = await fetchProfileWithRetry(3);
      if (profileResult.ok) {
        return { success: true };
      }
      return {
        success: false,
        error: 'Signed in successfully, but our server is temporarily unavailable. Please try again in a moment.',
      };
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user') {
        return { success: false, error: 'Sign-in popup was closed' };
      }
      if (error?.code === 'auth/popup-blocked') {
        return { success: false, error: 'Popup was blocked by your browser. Please allow popups for this site.' };
      }
      console.error('Google login error:', error);
      return { success: false, error: 'Google login failed. Please try again.' };
    }
  }, [fetchProfileWithRetry]);

  const logout = useCallback(() => {
    signOut(auth).catch(() => {});
    api.setAuthToken(null);
    localStorage.removeItem('isGuest');

    // Clear all japa-related localStorage data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('japa-') || key === 'japa_pending_sync')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear life purpose session data
    sessionStorage.removeItem('lifePurposeResult');
    sessionStorage.removeItem('lifePurposeProfile');

    setState({
      user: null,
      japaStats: null,
      lifePurposeProfile: null,
      hasLifePurposeAccess: false,
      canRetakeQuestionnaire: false,
      isAuthenticated: false,
      isLoading: false,
      isGuest: false,
    });
  }, []);

  const verifyAge = useCallback(async (dateOfBirth: string) => {
    try {
      await setFirebaseToken();
      const response = await authApi.verifyAge(dateOfBirth);
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          user: prev.user ? { ...prev.user, isAgeVerified: response.data!.isAgeVerified } : null,
        }));
        return { success: true, isAgeVerified: response.data.isAgeVerified };
      }
      return { success: false, isAgeVerified: false };
    } catch {
      return { success: false, isAgeVerified: false };
    }
  }, [setFirebaseToken]);

  const refreshProfile = useCallback(async () => {
    try {
      await setFirebaseToken();
      await fetchAndSetProfile();
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  }, [setFirebaseToken, fetchAndSetProfile]);

  const setGuestMode = useCallback((isGuest: boolean) => {
    if (isGuest) {
      localStorage.setItem('isGuest', 'true');
    } else {
      localStorage.removeItem('isGuest');
    }
    setState(prev => ({ ...prev, isGuest }));
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    register,
    loginWithGoogle,
    logout,
    verifyAge,
    refreshProfile,
    setGuestMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;